import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import {
  Building,
  Error404,
  Nodata,
  Noreply,
  Notoken,
  Unconnect
} from '@/assets/images/nodata'
import { useWallet } from '@/hooks/useWallet'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/routes'

import { Flex } from './Box'
import { Button } from './Button'
import { SilkscreenText } from './Text'

export const Placeholder: React.FC<
  {
    text?: React.ReactNode
    img?: React.ReactNode
  } & React.HTMLAttributes<HTMLDivElement>
> = ({ className, img, text, ...props }) => {
  const renderText = useCallback(() => {
    if (!text) return null
    if (typeof text === 'string')
      return (
        <SilkscreenText className="text-center text-xs xl:text-base">
          {text}
        </SilkscreenText>
      )
    return text
  }, [text])

  return (
    <Flex
      className={cn('flex-col items-center justify-center', className)}
      {...props}
    >
      {img}
      {renderText()}
    </Flex>
  )
}

export const NoReply: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-reply"
      img={<img src={Noreply} className="size-40 xl:size-[200px]" />}
      text={t('nodata.reply')}
      {...props}
    />
  )
}

export const NoConnect: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()
  const { connect } = useWallet()

  return (
    <Placeholder
      role="no-connect"
      img={<img src={Unconnect} className="size-40 xl:size-[200px]" />}
      text={
        <Flex className="flex-col">
          <SilkscreenText className="text-center text-xs xl:text-base">
            {t('nodata.unconnect')}
          </SilkscreenText>
          <Button
            link
            size="xs"
            className="block font-Silkscreen underline"
            onClick={connect}
          >
            {t('nodata.unconnect.connect')}
          </Button>
        </Flex>
      }
      {...props}
    />
  )
}

export const NoToken: React.FC<
  React.ComponentProps<typeof Placeholder> & {
    tipsText?: string
  }
> = ({ tipsText, ...props }) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-token"
      img={<img src={Notoken} className="size-40 xl:size-[280px]" />}
      text={
        <Flex className="flex-col items-center">
          <SilkscreenText className="text-center text-xs xl:text-base">
            {tipsText ?? t('nodata.notFoundToken')}
          </SilkscreenText>
          <NavLink
            to={RoutePath.CREATE}
            className="font-Silkscreen text-sm text-primary underline"
          >
            {t('nodata.notoken.start')}
          </NavLink>
        </Flex>
      }
      {...props}
    />
  )
}

export const ComingSoon: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="coming-soon"
      img={<img src={Building} className="w-full xl:w-[538px]" />}
      text={
        <Flex className="mt-4 flex-col items-center">
          <SilkscreenText className="text-center text-xs xl:text-xl">
            {t('nodata.comingsoon')}
          </SilkscreenText>
          <SilkscreenText className="text-center text-xs xl:text-base">
            {t('nodata.comingsoon.desc')}
          </SilkscreenText>
        </Flex>
      }
      {...props}
    />
  )
}

export const NoData: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-data"
      img={<img src={Nodata} className="size-40 xl:size-[280px]" />}
      text={t('nodata.normal')}
      {...props}
    />
  )
}

export const NoFound: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-found"
      img={<img src={Error404} className="size-40 xl:size-[280px]" />}
      text={t('nodata.404')}
      {...props}
    />
  )
}
